import React, { useEffect, useState } from 'react';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import apiFetch from '../../../utils/apiFetch';

/**
 * The DM gives or takes back Heroic Inspiration (2024): one per character, saved on the server. `renderTrigger(open)`
 * draws the opener (the DM Tools menu item); without it a button.
 */
export default function HeroicInspirationControls({ campaignId, renderTrigger }) {
  const [open, setOpen] = useState(false), [roster, setRoster] = useState(null);
  const [error, setError] = useState(''), [busyId, setBusyId] = useState(null);
  useEffect(() => { setOpen(false); setRoster(null); setError(''); }, [campaignId]);
  useEffect(() => {
    if (!open) return undefined;
    let live = true; setRoster(null); setError('');
    (async () => {
      try {
        const response = await apiFetch(`/campaigns/${encodeURIComponent(campaignId)}/rests`, { cache: 'no-store' });
        const data = await response.json(); if (!response.ok) throw new Error(data.message || 'Could not load characters.');
        if (live) setRoster(data.characters || []);
      } catch (failure) { if (live) setError(failure.message); }
    })();
    return () => { live = false; };
  }, [open, campaignId]);
  const award = async (character, has) => {
    if (busyId) return;
    setBusyId(character.id); setError('');
    try {
      const response = await apiFetch(`/characters/${encodeURIComponent(character.id)}/heroic-inspiration`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ has, revision: character.heroicInspiration?.revision || 0 }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || 'Heroic Inspiration could not be changed.');
      const state = { has: result.heroicInspiration?.has === true, revision: Number(result.heroicInspiration?.revision) || 0 };
      setRoster(previous => previous?.map(c => (c.id === character.id ? { ...c, heroicInspiration: state } : c)));
    } catch (failure) { setError(failure.message); }
    finally { setBusyId(null); }
  };
  return <>
    {renderTrigger ? renderTrigger(() => setOpen(true)) : <Button variant="outline-light" size="sm" onClick={() => setOpen(true)}>Heroic Inspiration</Button>}
    <Modal scrollable show={open} onHide={() => !busyId && setOpen(false)} centered className="dnd-modal modern-modal">
      <Modal.Header closeButton={!busyId}><Modal.Title>Heroic Inspiration</Modal.Title></Modal.Header>
      <Modal.Body>
        <p>Award Heroic Inspiration for great roleplay or heroics. A character holds one at a time and spends it to reroll a d20 of their own.</p>
        {error && <Alert variant="danger">{error}</Alert>}
        {!roster && !error && <Spinner role="status" animation="border"><span className="visually-hidden">Loading characters</span></Spinner>}
        {roster && !roster.length && <p>No characters in this campaign.</p>}
        {roster?.map(c => {
          const has = c.heroicInspiration?.has === true;
          return <div key={c.id} className="d-flex align-items-center justify-content-between gap-2 mb-2">
            <span>{c.name} · {has ? 'Has Heroic Inspiration' : 'None'}</span>
            <Button size="sm" variant={has ? 'outline-light' : 'warning'} disabled={Boolean(busyId)} onClick={() => award(c, !has)}>
              {busyId === c.id ? 'Saving…' : has ? 'Remove' : 'Give'}
            </Button>
          </div>;
        })}
      </Modal.Body>
      <Modal.Footer><Button variant="secondary" disabled={Boolean(busyId)} onClick={() => setOpen(false)}>Close</Button></Modal.Footer>
    </Modal>
  </>;
}
